"use client";

import { Card } from "@/components/ui/card";
import { formatCentsWithCurrency } from "@/lib/formatting/currency";
import { formatDate } from "@/lib/formatting/dates";
import { formatChange } from "@/lib/price-index/format-change";
import { RangeBar } from "./range-bar";
import { useGradeSelection } from "./grade-selection";

export type QuotePricing = {
  avgPriceCents: number;
  typicalLowCents: number;
  typicalHighCents: number;
  changePercent: number | null;
  computedAt?: number;
};

export type PriceComparison = { label: string; avgPriceCents: number };

function comparisonText(
  priceCents: number,
  comparison: PriceComparison,
): string {
  const percent = Math.round(
    ((priceCents - comparison.avgPriceCents) / comparison.avgPriceCents) * 100,
  );
  if (percent === 0) return `Same as the ${comparison.label}`;
  return `${Math.abs(percent)}% ${percent > 0 ? "above" : "below"} the ${comparison.label}`;
}

function detailLine(input: {
  gradeLabel: string;
  pricing: QuotePricing;
  comparison: PriceComparison | null;
  priceCents: number;
  showChange: boolean;
}): string {
  const parts = [input.gradeLabel];
  if (input.comparison) {
    parts.push(comparisonText(input.priceCents, input.comparison));
  }
  if (input.showChange && input.pricing.changePercent !== null) {
    parts.push(`${formatChange(input.pricing.changePercent)} vs last month`);
  }
  if (input.pricing.computedAt) {
    parts.push(
      `Updated ${formatDate(input.pricing.computedAt, { month: "short", day: "numeric", year: "numeric" })}`,
    );
  }
  return parts.join(" · ");
}

function PriceFigure({
  priceCents,
  currency,
  detail,
}: {
  priceCents: number;
  currency: "USD" | "CAD";
  detail: string;
}) {
  return (
    <div>
      <p className="tabular-nums text-5xl font-semibold text-foreground">
        {formatCentsWithCurrency(priceCents, currency)}
        <span className="ml-2 font-sans text-base font-normal text-fg-muted">
          each
        </span>
      </p>
      <p className="mt-2 text-sm text-fg-muted">{detail}</p>
    </div>
  );
}

function useQuoteFigures(
  pricing: QuotePricing,
  comparison: PriceComparison | null,
  gradeScopeName: string | null,
) {
  const { selected } = useGradeSelection();
  const priceCents = selected?.avgPriceCents ?? pricing.avgPriceCents;
  const detail = detailLine({
    gradeLabel: selected
      ? `${selected.label}${gradeScopeName ? `, ${gradeScopeName} figure` : ""}`
      : "All grades",
    pricing,
    comparison: selected ? null : comparison,
    priceCents,
    showChange: !selected,
  });
  return {
    selected,
    priceCents,
    lowCents: selected ? selected.typicalLowCents : pricing.typicalLowCents,
    highCents: selected ? selected.typicalHighCents : pricing.typicalHighCents,
    detail,
  };
}

export function PriceQuote({
  pricing,
  comparison,
  currency,
  gradeScopeName,
}: {
  pricing: QuotePricing;
  comparison: PriceComparison | null;
  currency: "USD" | "CAD";
  gradeScopeName: string | null;
}) {
  const { priceCents, lowCents, highCents, detail } = useQuoteFigures(
    pricing,
    comparison,
    gradeScopeName,
  );
  return (
    <Card padding="roomy">
      <div
        aria-live="polite"
        className="grid gap-6 md:grid-cols-[minmax(0,1fr)_18rem] md:items-center"
      >
        <PriceFigure
          priceCents={priceCents}
          currency={currency}
          detail={detail}
        />
        {lowCents !== undefined && highCents !== undefined && lowCents < highCents && (
          <RangeBar
            label="Typical price range"
            lowCents={lowCents}
            highCents={highCents}
            valueCents={priceCents}
            currency={currency}
          />
        )}
      </div>
    </Card>
  );
}
